import ChangeStore from "../store";
import { action } from "satcheljs/lib/legacy";
import { ChangeSource, FlightKeys, IChangeEntity } from "sh-models";
import { ECSConfigKey, ECSConfigService, FlightSettingsService } from "sh-services";

/**
 * Clear the currently tracked changes.
 *
 * Most call sites for this action are view-options / navigation changes (calendar type picker,
 * Today button, date pick, employee view, grouped view, show profile pics, staff per 30, etc.).
 * Those clears exist because most change sources are tied to whatever range / view the user is
 * currently looking at, so an Undo issued after navigating away would target stale state.
 *
 * When the Assign Open Shifts feature is enabled (ECSConfigKey.EnableAssignOpenShifts), AOS
 * entries are preserved across these clears. The AOS revert task in `assignOpenShiftsChangeTasks.ts`
 * snapshots the assigned `{teamId, tagId, startDate, endDate}` at construction and refreshes that
 * exact range on undo, so it remains valid no matter what the user navigates to — the hamburger
 * Undo / Redo, Ctrl+Z / Ctrl+Y, and the toast Undo button keep working after a view change.
 * When the feature flag is off, this action falls back to the legacy behavior of fully wiping
 * both stacks so existing scenarios are not affected.
 *
 * Full wipes for team switch and time zone change go through `resetChangeStore` (not this
 * action), so those always drop AOS entries — which is correct, the revert can't apply to a
 * different team.
 */
export let clearTrackedChanges = action("clearTrackedChanges")(
    function clearTrackedChanges() {
        if (!FlightSettingsService.isFlightEnabled(FlightKeys.EnableShiftUndoWeb)) {
            return;
        }
        if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableAssignOpenShifts)) {
            // Feature off: legacy behavior — wipe both stacks completely so callers see exactly
            // the same state they did before the AOS preserve was introduced.
            ChangeStore().undoStack.clear();
            ChangeStore().redoStack.clear();
            return;
        }
        const isAssignOpenShiftsGroup = (group: Array<IChangeEntity>): boolean =>
            !!group && group.length > 0 && group.every(c => c && c.changeSource === ChangeSource.AssignOpenShifts);
        ChangeStore().undoStack.replace(ChangeStore().undoStack.filter(isAssignOpenShiftsGroup));
        ChangeStore().redoStack.replace(ChangeStore().redoStack.filter(isAssignOpenShiftsGroup));
    });
